import React, { useCallback, useEffect } from 'react';
import { useAtom } from 'jotai/index';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';
import classNames from 'classnames';
import styles from '../pages/Widget/css/index.module.scss';
import {
  widgetDataAtom,
  widgetDefaultsAtom,
  widgetGettingLoadingAtom,
  widgetInputsAtom,
  widgetParamsAtom, widgetScreenStateAtom,
  widgetSelectedLanguageAtom, widgetSuccessScreenTextAtom, widgetWithoutCloseAtom, widgetQueryParamsAtom,
} from '../atoms/widgetAtoms';
import sendRequest from '../helpers/sendRequest';
import Api from '../api';
import { closeModalAtom, modalStateAtom, openInfoModalAtom } from '../atoms/globalAtoms';
import widgetLanguages from '../pages/Widget/constants/widgetLanguages';
import PartnersServices from '../services/PartnersServices';
import WT from '../pages/Widget/constants/widgetTranslations';
import WidgetsServices from '../services/WidgetsServices';
import widgetStates from '../pages/Widget/constants/widgetStates';
import WidgetCloseModal from '../pages/Widget/components/WidgetCloseModal';
import sleep from "../helpers/sleep";

function UseWidget() {
  const [widgetData, setWidgetData] = useAtom(widgetDataAtom);
  const [widgetParams, setWidgetParams] = useAtom(widgetParamsAtom);
  const [widgetDefaults, setWidgetDefaults] = useAtom(widgetDefaultsAtom);
  const [, setWidgetWithoutClose] = useAtom(widgetWithoutCloseAtom);
  const [, setWidgetGettingLoading] = useAtom(widgetGettingLoadingAtom);
  const [widgetInputs, setWidgetInputs] = useAtom(widgetInputsAtom);
  const [widgetSelectedLanguage, setWidgetSelectedLanguage] = useAtom(widgetSelectedLanguageAtom);
  const [, openInfoModal] = useAtom(openInfoModalAtom);
  const [, setModalState] = useAtom(modalStateAtom);
  const [, closeModal] = useAtom(closeModalAtom);
  const [widgetScreenState, setWidgetScreenState] = useAtom(widgetScreenStateAtom);
  const [, setWidgetSuccessScreenText] = useAtom(widgetSuccessScreenTextAtom);
  const [, setWidgetQueryParams] = useAtom(widgetQueryParamsAtom);

  const [searchParams] = useSearchParams();
  const defaultDataQuery = Object.fromEntries([...searchParams]);

  const getSlots = async () => {
    const payload = {
      id: widgetData.id,
      date: typeof widgetParams.date === 'string' ? widgetParams.date : moment(widgetParams.date).format('YYYY-MM-DD'),
      persons: widgetParams.persons,
      locale: widgetSelectedLanguage.code,
    };

    setWidgetGettingLoading(true);

    const data = await sendRequest({
      request: Api.getWidgetSlots,
      payload,
      warnErrorText: 'while getting widget slots',
      showErrorInModal: false,
    });

    setTimeout(() => setWidgetGettingLoading(false), 20);

    setWidgetInputs({ slot: null });

    if (data?.status !== 'ok') {
      if (data?.message?.length) {
        setWidgetData({
          slots: [],
          message: data.message,
        });
      } else {
        setWidgetData({
          slots: [],
          message: 'Возникла ошибка',
        });
      }
      return
    }

    setWidgetData({
      name: data.data.address.name,
      max_guests: data.data.address.max_guests,
      slots: data.data.time_slots,
      message: data.message,
      send_through_telegram_bot: data.data.send_through_telegram_bot === true,
      telegram_bot_name: data.data.telegram_bot_name || '',
      widget_telegram_bot_description: data.data.widget_telegram_bot_description || '',
    });
  };

  const initWidget = async (addressId) => {
    const data = await sendRequest({
      request: Api.initWidget,
      payload: { id: addressId },
      warnErrorText: 'while init widget',
      showErrorInModal: false,
    });

    if (data?.status !== 'ok') return;

    const objForDefaults = {
      languagesList: data.data.locales,
    };
    if (!defaultDataQuery?.managerPhone) {
      objForDefaults.managerPhone = WidgetsServices.convertPhoneNumber(
        data.data.address.phone,
        data.data.address.phone_prefixed,
      );
    }
    setWidgetDefaults(objForDefaults);

    setWidgetData({
      name: data.data.address.name,
      address: data.data.address.descr,
      companyName: data.data.company,
      max_guests: data.data.address.max_guests,
    });
  };

  const makeBook = async ({ captchaToken }) => {
    const payload = {
      id: widgetData.id,
      client: {
        name: widgetInputs.name,
        phone: widgetInputs.phoneNumber.replaceAll(' ', '').slice(1),
      },
      comments: widgetInputs.comment,
      // reservation_at: `${moment(widgetParams.date).format('YYYY-MM-DD')}T${PartnersServices.convertSecondsToFormat(widgetInputs.slot)}:00`,
      date: moment(widgetParams.date).format('YYYY-MM-DD'),
      time_slot: widgetInputs.slot,
      persons: widgetParams.persons,
      retoken: captchaToken,
      tag: widgetParams.tag || ''
    };

    const data = await sendRequest({
      request: Api.makeWidgetBook,
      payload,
      warnErrorText: 'while making widget book',
      showErrorInModal: false,
      setLoading: setWidgetGettingLoading,
    });

    if (data?.status !== 'ok') {
      if (data?.data?.display_phone === true) openInfoModal({ text: `${data?.message} ${widgetDefaults.managerPhone}` });
      else openInfoModal({ text: data?.message });
      return;
    }

    setWidgetScreenState(widgetStates.success);
    setWidgetSuccessScreenText(data?.message);
  };

  const closeWidget = () => {
    window.parent.postMessage({ action: 'close' }, '*');
  };

  const repeatWidget = () => {
    window.location.reload();
  };

  const translate = useCallback((wtObject, ...p) => {
    if (!widgetSelectedLanguage?.code) return;
    let fn = wtObject[widgetSelectedLanguage.code];
    if (typeof fn !== 'function') fn = wtObject[widgetLanguages.en.code];

    return fn(...p);
  }, [widgetSelectedLanguage]);

  const requestToCloseWidget = useCallback(() => {
    if (widgetScreenState === widgetStates.success) {
      return closeWidget();
    }

    setModalState({
      isOpen: true,
      children: <WidgetCloseModal
        t={translate}
        onClose={closeWidget}
        onCancel={closeModal}
      />,
      closeTimeoutMS: 0,
      style: {
        top: '148px', height: '162px', width: '480px', maxWidth: '100vw', borderTopLeftRadius: 0, borderTopRightRadius: 0,
      },
      overlayStyle: { backgroundColor: 'rgba(0,0,0,0.07)' },
    });
  }, [translate, widgetScreenState]);

  const initWidgetData = () => {
    try {
      if (!defaultDataQuery) return;

      if (!defaultDataQuery?.restaurantId) {
        return openInfoModal({
          text: 'Отсутствует поле "data-restaurant-id"',
        });
      }
      handleDefaultData(defaultDataQuery);
      setWidgetQueryParams(defaultDataQuery);
      setWidgetData({
        id: defaultDataQuery.restaurantId,
        buttonClassName: classNames(styles.myButton),
      });

      initWidget(defaultDataQuery.restaurantId).then();
    } catch (e) {
      console.warn(e);
    }
  };

  const handleDefaultData = (defaultData) => {
    if (defaultData.reserveDate && WidgetsServices.isValidAndFutureOrToday(defaultData.reserveDate)) {
      setWidgetParams({
        date: new Date(moment(defaultData.reserveDate, 'YYYY-MM-DD')),
      });
    }
    if (isFinite(defaultData.reserveGuests)) {
      setWidgetParams({
        persons: +defaultData.reserveGuests,
      });
    }
    if (defaultData.userName) {
      setWidgetInputs({
        name: defaultData.userName,
      });
    }
    if (defaultData.tag) {
      setWidgetParams({
        tag: defaultData.tag,
      });
    }
    if (defaultData.userPhone) {
      setWidgetInputs({
        phoneNumber: WidgetsServices.convertPhoneNumber(defaultData.userPhone),
      });
    }
    if (defaultData.managerPhone) {
      setWidgetDefaults({
        managerPhone: WidgetsServices.convertPhoneNumber(defaultData.managerPhone),
      });
    }
    if (defaultData.additionalInfo) {
      setWidgetInputs({
        comment: defaultData.additionalInfo,
      });
    }
    if (defaultData.withoutClose) {
      setWidgetWithoutClose(defaultData.withoutClose === 'true');
    }
    if (widgetLanguages[defaultData.interfaceLanguage]) {
      setWidgetSelectedLanguage(widgetLanguages[defaultData.interfaceLanguage]);
    }
    if (WidgetsServices.checkIfColorIsValid(defaultData.colorMain)) {
      document.documentElement.style.setProperty('--widget-main-color', defaultData.colorMain);
    }
    if (WidgetsServices.checkIfColorIsValid(defaultData.colorBackground)) {
      document.documentElement.style.setProperty('--widget-background-color', defaultData.colorBackground);
    }
    if (WidgetsServices.checkIfColorIsValid(defaultData.colorSecondary)) {
      document.documentElement.style.setProperty('--widget-secondary-color', defaultData.colorSecondary);
    }
    if (WidgetsServices.checkIfColorIsValid(defaultData.colorThird)) {
      document.documentElement.style.setProperty('--widget-third-color', defaultData.colorThird);
    }
    if (WidgetsServices.checkIfColorIsValid(defaultData.colorText)) {
      document.documentElement.style.setProperty('--widget-text-color', defaultData.colorText);
    }
    if (WidgetsServices.checkIfColorIsValid(defaultData.colorButton)) {
      document.documentElement.style.setProperty('--widget-button-color', defaultData.colorButton);
    }
  };

  return {
    t: translate,
    initWidgetData,
    getSlots,
    makeBook,
    requestToCloseWidget,
    closeWidget,
    repeatWidget,
  };
}

export default UseWidget;
